import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Modal, QuestionStatus, QuestionType, Quiz as QuizComponent } from '@atlassian/learning-components';
import { useLoaderData, useParams } from 'react-router-dom';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { css } from '@compiled/react';

import { useProgressState } from 'src/store/progress/progressStore';
import { ContentfulSmQuestion } from 'src/graphql/queries/GetSmLesson';
import { getInspectModeData } from 'src/utils/contentful';
import { sendUIEvent } from 'src/utils/analytics';
import { getAssetUrl, loginUser } from 'src/services/http/user';
import { usePreviewMode } from 'src/hooks/usePreviewMode';
import { getSlug } from 'src/utils/slug';
import { LearningProgressStatus } from 'src/services/http/progress';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';

import {
  getMatchingQuestions,
  handleAnswerSubmit,
  handleInteraction,
  handleUnload,
  getUncompletedQuestionsAndStepAsync,
  sendCompletionAnalytics,
} from './utils/quizUtils';
import { useLoginModalFlag } from './hooks/useLoginModalFlag';

export interface QuizProps {
  onLessonCompletion: () => void;
}

export enum QuizStatus {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  UNINITIALIZED = 'UNINITIALIZED',
}

export const Quiz: React.FC<QuizProps> = ({ onLessonCompletion }) => {
  const [quizStatus, setQuizStatus] = useState<QuizStatus>(QuizStatus.UNINITIALIZED);
  const { course, lesson } = useLoaderData() as LessonLoaderProps;
  const { user, isLoading } = useAtlassianUser();

  const [quizStep, setQuizStep] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState<ContentfulSmQuestion[]>([]);
  const preview = usePreviewMode();
  const { courseId, lessonId } = useParams();
  const quizAbandonedAnalyticsAttributes = useRef<null | Record<string, string | number | undefined>>(null);
  const quizInteractionAnalytics = useRef<null | {
    lessonId?: string;
    startTimeMs: number;
    notStarted: boolean;
    isDirty: boolean;
  }>(null);

  const [{ isLoadingProgressForCourses, progressForLessons }, { fetchQuizProgress, completeLesson, startLesson }] =
    useProgressState();

  const questions = useMemo(
    () => getMatchingQuestions(quizQuestions, preview, lesson, user),
    [quizQuestions, preview, lesson, user],
  );

  useEffect(() => {
    if (!lesson.quiz || quizStatus !== QuizStatus.UNINITIALIZED || isLoading) {
      return;
    }

    if (user && lesson) {
      startLesson(lesson, user);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, lesson, quizStatus, isLoading]);

  useEffect(() => {
    if (user && lesson && !isLoadingProgressForCourses) {
      if (!lesson.quiz || quizStatus !== QuizStatus.UNINITIALIZED) {
        return;
      }

      const lessonProgress = progressForLessons?.lessons.find((p) => p.lessonId === lesson.sys.id);
      const quizSection = lessonProgress?.section?.find((s) => s.sectionId === 'quiz');

      if (quizSection?.status === LearningProgressStatus.COMPLETED) {
        setQuizStatus(QuizStatus.COMPLETED);
        setQuizQuestions(lesson?.quiz?.questionsCollection.items ?? []);

        return;
      }

      fetchQuizProgress(lesson, course, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, lesson, user, progressForLessons]);

  useEffect(() => {
    setQuizStatus(QuizStatus.UNINITIALIZED);
    setQuizQuestions([]);
    setQuizStep(0);

    quizInteractionAnalytics.current = {
      lessonId: lesson.sys.id,
      startTimeMs: quizInteractionAnalytics.current ? quizInteractionAnalytics.current?.startTimeMs : 0,
      notStarted: false,
      isDirty: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.sys.id]);

  useEffect(() => {
    const fetchQuestions = async (): Promise<void> => {
      const isQuizNotReadyToInitialize = quizStatus !== QuizStatus.UNINITIALIZED || !lesson.quiz;

      if (isQuizNotReadyToInitialize) {
        return;
      }

      if (!user && !isLoading) {
        setQuizStatus(QuizStatus.IN_PROGRESS);
        setQuizQuestions(lesson.quiz.questionsCollection.items);

        return;
      }

      if (progressForLessons) {
        const lessonProgress = progressForLessons.lessons.find(
          (lessonProgress) => lessonProgress.lessonId === lesson.sys.id,
        );
        const quizSection = lessonProgress?.section?.find((s) => s.sectionId === 'quiz');

        if (quizSection?.status === LearningProgressStatus.COMPLETED) {
          setQuizStatus(QuizStatus.COMPLETED);
          setQuizQuestions(lesson?.quiz?.questionsCollection.items ?? []);

          return;
        }

        const { questions, step, isClean } = await getUncompletedQuestionsAndStepAsync(user, lesson);

        setQuizStep(step === -1 ? 0 : step + 1);
        setQuizQuestions(questions);
        setQuizStatus(QuizStatus.IN_PROGRESS);

        if (isClean) {
          quizInteractionAnalytics.current = {
            lessonId: lesson.sys.id,
            startTimeMs: 0,
            notStarted: true,
            isDirty: false,
          };
        }
      }
    };

    fetchQuestions();
  }, [course, lesson, progressForLessons, quizStatus, user, isLoadingProgressForCourses, isLoading]);

  useEffect(() => {
    if (!progressForLessons || !lesson) {
      return;
    }

    const lessonProgress = progressForLessons.lessons.find((p) => p.lessonId === lesson.sys.id);

    if (!lessonProgress) {
      return;
    }

    const isLessonCompleted = lessonProgress.status === LearningProgressStatus.COMPLETED;
    const quizSection = lessonProgress.section?.find((s) => s.sectionId === 'quiz');
    const isQuizCompleted = quizSection?.status === LearningProgressStatus.COMPLETED;

    if ((isLessonCompleted || isQuizCompleted) && quizStatus !== QuizStatus.COMPLETED) {
      setQuizStatus(QuizStatus.COMPLETED);
      setQuizQuestions(lesson?.quiz?.questionsCollection.items ?? []);
    }
  }, [progressForLessons, lesson, quizStatus]);

  const onLoginButtonClick = useCallback((): void => {
    sendUIEvent('lessonScreen', 'button', 'clicked', 'signInButton', { objectType: 'modal' });
    loginUser();
  }, []);

  const { isLoginModalVisible, handleUserLogin, onCloseButtonClick } = useLoginModalFlag(user);

  const onQuizCompletedChange = (completed: boolean): void => {
    setQuizStatus(completed ? QuizStatus.COMPLETED : QuizStatus.IN_PROGRESS);
  };

  useEffect(() => {
    const handleUnloadWithParams = handleUnload.bind(null, {
      lesson,
      user,
      quizInteractionAnalytics,
      quizAbandonedAnalyticsAttributes,
    });

    window.addEventListener('beforeunload', handleUnloadWithParams);

    return () => {
      handleUnloadWithParams();

      window.removeEventListener('beforeunload', handleUnloadWithParams);
    };
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLessonCompletion = useCallback(async () => {
    if (!preview) {
      await completeLesson(lesson, course, user);
      onLessonCompletion();
    }

    setQuizStatus(QuizStatus.COMPLETED);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseId,
    lessonId,
    course,
    lesson,
    user,
    preview,
    onLessonCompletion,
    setQuizStatus,
    quizInteractionAnalytics,
    progressForLessons,
    isLoadingProgressForCourses,
  ]);

  useEffect(() => {
    if (!progressForLessons || quizInteractionAnalytics.current?.lessonId !== lesson.sys.id) {
      return;
    }

    const lessonProgress = progressForLessons.lessons.find((p) => p.lessonId === lesson.sys.id);

    if (lessonProgress?.status === LearningProgressStatus.COMPLETED) {
      sendCompletionAnalytics(course, lesson, progressForLessons, user, quizInteractionAnalytics.current);
    }
  }, [progressForLessons, course, lesson, user, quizInteractionAnalytics]);

  useEffect(() => {
    handleUnload({
      lesson: lesson,
      user: user,
      quizInteractionAnalytics: quizInteractionAnalytics,
      quizAbandonedAnalyticsAttributes: quizAbandonedAnalyticsAttributes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, handleUnload]);

  useEffect(() => {
    const quizCompletionPercentage = Math.round((quizStep / quizQuestions.length) * 100);

    if (user && quizStatus === QuizStatus.IN_PROGRESS && quizCompletionPercentage > 0) {
      quizAbandonedAnalyticsAttributes.current = {
        courseSlug: courseId,
        lessonSlug: lessonId,
        quizCompletionPercentage,
      };
    }
  }, [user, courseId, lessonId, quizStep, quizQuestions, quizStatus]);

  const isQuizLoaded =
    quizStatus !== QuizStatus.UNINITIALIZED && lessonId === getSlug(lesson) && !!quizQuestions.length;

  return (
    <>
      {isQuizLoaded ? (
        <QuizComponent
          lessonId={lessonId}
          isLoggedInUser={!!user}
          handleUserLogin={handleUserLogin}
          inspectMode={getInspectModeData(lesson.quiz, ['title'])}
          title={lesson.quiz.title}
          onCompletion={handleLessonCompletion}
          isCompleted={quizStatus === QuizStatus.COMPLETED}
          setIsCompleted={onQuizCompletedChange}
          step={quizStep}
          setStep={setQuizStep}
          questions={questions}
          onAnswerSubmit={(type: QuestionType, status: QuestionStatus) =>
            handleAnswerSubmit(type, status, courseId, lessonId, quizStep, user)
          }
          onInteraction={handleInteraction.bind(null, quizInteractionAnalytics, quizStatus, lesson)}
        />
      ) : (
        <div css={css({ display: 'flex', justifyContent: 'center' })}>
          <Spinner size="large" />
        </div>
      )}
      {isLoginModalVisible && (
        <ModalTransition>
          <Modal
            heading="Get the credit you deserve"
            subheading={
              <p>
                Log in with your Atlassian account to track your progress, access your learning history, and earn badges
                as you go.
              </p>
            }
            imageLink={getAssetUrl('/images/badgeModalImage.svg')}
            closeModal={onCloseButtonClick}
            actionButton={{
              label: 'Log in',
              onClick: onLoginButtonClick,
              iconBefore: <Icon type="atlassian-logo" />,
            }}
            width="large"
          />
        </ModalTransition>
      )}
    </>
  );
};
